import { Button } from "primereact/button";
import { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Toast, ToastMessage } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { useTranslation } from "react-i18next";
import RawMaterialService from "../../../services/RawMaterials/RawMaterialService";

interface RawMaterial {
  Id: number;
  name: string;
  hsncode: string;
  rmcategoryId: number;
  measurementTypeId?: number;
}

const RawMaterialSearch: React.FC<{ onSearch: (keyword: string) => void }> = ({ onSearch }) => {
  const { t } = useTranslation();
  const [selectedKeyword, setSelectedKeyword] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [name, setName] = useState("");
  const [hsn, setHsn] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [measurementTypeId, setMeasurementTypeId] = useState<number | null>(null);
  const toast = useRef<Toast>(null);
  const [nameError, setNameError] = useState("");
  const [categories, setCategories] = useState<any[]>([]);
  const [measurementTypes, setMeasurementTypes] = useState<any[]>([]);
  const [categoryError, setCategoryError] = useState("");
  const [measurementError, setMeasurementError] = useState("");
  const [hsnError, setHsnError] = useState("");

  const showMessage = (label: string, message: string, severity: ToastMessage["severity"]) => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const handleSearch = () => {
    onSearch(selectedKeyword);
  };

  const resetSearchFields = () => {
    setSelectedKeyword("");
    onSearch("");
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      const categoriesResponse = await RawMaterialService.RMCategories();
      const measurementTypesResponse = await RawMaterialService.RMMeasurementtypes();

      if (categoriesResponse) {
        setCategories(
          categoriesResponse.map((c: any) => ({
            label: c.categoryName,
            value: c.categoryID
          }))
        );
      }

      if (measurementTypesResponse) {
        setMeasurementTypes(
          measurementTypesResponse.map((m: any) => ({
            label: m.measurementTypeName,
            value: m.measurementTypeID    
          }))
        );
      }
    };

    fetchDropdownData();
  }, []);
  
  const handleOnSuccess = async () => {
    let hasError = false;

    if (!name.trim()) {
      setNameError("Name is required.");
      hasError = true;
    } else {
      setNameError("");
    }

    if (!categoryId) {
      setCategoryError("Category is required.");
      hasError = true;
    } else {
      setCategoryError("");
    }

    if (!measurementTypeId) {
      setMeasurementError("Measurement Type is required.");
      hasError = true;
    } else {
      setMeasurementError("");
    }

    if (hasError) return;

    try {
      const response = await RawMaterialService.addRawMaterial({
        name,
        hsncode: hsn,
        rmcategoryId: categoryId ?? 0,
        measurementTypeId: measurementTypeId ?? undefined,
      });

      if (response.isSuccess) {
        showMessage("Add Raw Material", "Raw Material added successfully", "success");
        window.location.reload();
      } else {
        showMessage("Add Raw Material", response.message || "Failed to add raw material", "error");
      }
    } catch (error) {
      console.error("Error adding raw material:", error);
      showMessage("Add Raw Material", "Unexpected error occurred.", "error");
    }
  };

  return (
    <div className="col-12">
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div className="p-input-icon-left" style={{ float: "left", width: "18%" }}>
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={selectedKeyword}
            onChange={(e) => setSelectedKeyword(e.target.value)}
            placeholder="Keyword Search"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ float: "left", width: "10%", marginLeft: "8px" }}>
          <Button style={{ width: "100%" }} onClick={handleSearch} severity="success">
            Filter
          </Button>
        </div>
        <div style={{ float: "left", width: "10%", marginLeft: "8px" }}>
          <Button style={{ width: "100%" }} onClick={resetSearchFields} severity="secondary" text raised>
            Reset
          </Button>
        </div>
        <div style={{ marginLeft: "12px", float: "right" }}>
          <Button className="bg-blue-700 text-white" onClick={() => setShowAddModal(true)}>
            Add Raw Material
          </Button>
        </div>

        <Dialog visible={showAddModal} className="quickview w-50" draggable={false} modal onHide={() => setShowAddModal(false)}>
          <div className="bg-green-600 text-white text-xl font-semibold  px-2 rounded-md  shadow text-center">
            <h5 className="text-center mb-1 text-white bg-green-600 py-2 px-2 rounded-md">
              ADD RAW MATERIAL
            </h5>
          </div>

          <div className="mb-3">
            <label>Raw Material Name <span className="required">*</span></label>
            <InputText
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (e.target.value.trim()) setNameError("");
              }}
              placeholder="Enter Raw Material Name"
             style={{ 
                width: "100%", 
                backgroundColor: "white",
                boxShadow: "0 0 0 0.1rem rgba(155, 177, 143, 0.25)"
              }} 
              autoFocus
            />
            {nameError && <small className="p-error">{nameError}</small>}
          </div>

          <div className="mb-3">
            <label>HSN Code<span className="required">*</span></label>
            <InputText 
              value={hsn} 
              onChange={(e) => setHsn(e.target.value)} 
              placeholder="Enter HSN Code"
              style={{ 
                width: "100%", 
                backgroundColor: "white",
                boxShadow: "0 0 0 0.1rem rgba(155, 177, 143, 0.25)"
              }} 
            />
          </div>

          <div className="mb-3">
            <label>Category<span className="required">*</span></label>
            <Dropdown
              value={categoryId}
              onChange={(e) => setCategoryId(e.value)}
              options={categories}
              placeholder="Select Category"
              filter
              filterMatchMode="startsWith"
              showFilterClear
              style={{ 
                width: "100%", 
                backgroundColor: "white",
                boxShadow: "0 0 0 0.1rem rgba(155, 177, 143, 0.25)"
              }} 
            />
          </div>
          
          <div className="mb-3">
            <label>Measurement Type<span className="required">*</span></label>
            <Dropdown
              value={measurementTypeId}
              onChange={(e) => setMeasurementTypeId(e.value)}
              options={measurementTypes}
              placeholder="Select Measurement"
             style={{ 
                width: "100%", 
                backgroundColor: "white",
                boxShadow: "0 0 0 0.1rem rgba(155, 177, 143, 0.25)"
              }} 
            />
          </div>

          <div className="text-right mt-4">
            <Button className="bg-green-700 text-white" onClick={handleOnSuccess}>
              Save
            </Button>
          </div>
        </Dialog>
        <Toast ref={toast} />
      </div>
    </div>
  );
};

export default RawMaterialSearch;